import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forkOutcome } from '@/api';
import { isLoggedIn } from '@/auth/token';
import PublicChrome from '../landing/components/PublicChrome';
import MatchChat from './MatchChat';
import { useCertifiedPlaybook } from './usePlaybook';
import {
  disciplineTabs,
  filterOutcomes,
  outcomePath,
  scoreBadgeColor,
  type PublicOutcomeSummary,
} from '../landing/outcomeShowcase';

export default function PlaybooksPage() {
  const navigate = useNavigate();
  const [outcomes, setOutcomes] = useState<PublicOutcomeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [discipline, setDiscipline] = useState('All');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [forkErr, setForkErr] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Run a playbook — Sourcetape';
    fetch('/api/outcomes/public')
      .then((res) => {
        if (!res.ok) throw new Error('failed');
        return res.json();
      })
      .then((data) => {
        setOutcomes(Array.isArray(data) ? data : []);
        setLoadError(null);
      })
      .catch(() => {
        setOutcomes([]);
        setLoadError('Could not reach the outcome registry.');
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterOutcomes(outcomes, { discipline, query });
  const disciplines = disciplineTabs(outcomes);

  const onUse = async (outcomeId: string) => {
    setForkErr(null);
    setBusyId(outcomeId);
    const result = await useCertifiedPlaybook({
      outcomeId,
      loggedIn: isLoggedIn(),
      navigate,
      forkApi: forkOutcome,
    });
    if (!result.ok && result.error) setForkErr(result.error);
    setBusyId(null);
  };

  return (
    <PublicChrome>
      <main className="max-w-7xl mx-auto px-5 py-10" data-testid="playbooks-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-accent font-mono font-semibold">Run a playbook</p>
            <h1 className="text-3xl font-semibold tracking-tight mt-1">Pick a certified outcome</h1>
            <p className="text-sm text-texts mt-2 max-w-xl">
              Chat about the job you want, or click one from the approved list. We fork it into Studio with the name already set. Then press Run.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/marketplace" className="text-sm text-texts hover:text-textp">
              Browse marketplace
            </Link>
            <Link to="/dashboard?view=projects" className="text-sm text-texts hover:text-textp">
              New playbook instead
            </Link>
          </div>
        </div>

        {forkErr && (
          <div className="mb-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{forkErr}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,22rem)_1fr] gap-6">
          <MatchChat onUse={onUse} busyId={busyId} />

          <section>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search outcomes…"
                className="bg-surface border border-border text-sm rounded-lg px-3 py-2 text-textp focus:outline-none focus:border-accent w-56"
              />
              <div className="flex flex-wrap gap-2">
                {disciplines.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDiscipline(d)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      discipline === d ? 'bg-accent text-accent-text' : 'bg-surface text-texts border border-border'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-texts py-12">Loading certified outcomes…</p>
            ) : loadError ? (
              <p className="text-sm text-texts py-12">{loadError}</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-texts py-12">No certified outcomes match. Start a New Playbook instead.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filtered.map((outcome) => {
                  const scoreVal = outcome.score?.overall ?? outcome.quality_score ?? 0;
                  const tone = scoreBadgeColor(scoreVal);
                  return (
                    <article key={outcome.id} className="bg-surface border border-border rounded-xl p-5 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-bg text-texts border border-border">
                          {outcome.discipline || 'General'}
                        </span>
                        <span
                          className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded ${
                            tone === 'emerald'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : tone === 'amber'
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                          }`}
                        >
                          {scoreVal.toFixed(1)}/12
                        </span>
                      </div>
                      <Link to={outcomePath(outcome)} className="mt-3 text-base font-semibold text-textp hover:text-accent">
                        {outcome.title}
                      </Link>
                      <p className="mt-1.5 text-xs text-texts line-clamp-2 flex-1">
                        {outcome.plain_summary || outcome.description || 'No description recorded.'}
                      </p>
                      <button
                        type="button"
                        data-testid="use-playbook"
                        disabled={busyId === outcome.id}
                        onClick={() => onUse(outcome.id)}
                        className="mt-4 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-3 py-2 rounded-md"
                      >
                        {busyId === outcome.id ? 'Opening…' : 'Use this playbook'}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
    </PublicChrome>
  );
}
